import React, { useState, useEffect } from 'react';
import { Box, Button, Code, VStack, Text, Heading, Alert, AlertIcon } from '@chakra-ui/react';
import supabase from '../utils/supabaseUtils';

export function DebugEventOrders() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const eventId = 'a8e12716-c78a-46a8-8cf8-1b30ac59cabd';

  const checkDatabase = async () => {
    setLoading(true);
    const results = {};
    
    try {
      console.log('=== DEBUGGING DASHBOARD QUERY DISCREPANCY ===');
      
      // 1. Raw count from event_orders (what you see in database)
      console.log('1. Checking raw event_orders count...');
      const { count: rawCount, error: rawCountError } = await supabase
        .from('event_orders')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', eventId);
      
      results.rawEventOrdersCount = rawCountError 
        ? `Error: ${rawCountError.message}` 
        : `Raw count: ${rawCount} records`;

      // 2. Get all event_orders records for this event
      console.log('2. Getting all event_orders records...');
      const { data: allEventOrders, error: allOrdersError } = await supabase
        .from('event_orders')
        .select('*')
        .eq('event_id', eventId);
      
      results.allEventOrdersRecords = allOrdersError 
        ? `Error: ${allOrdersError.message}` 
        : {
            count: allEventOrders?.length || 0,
            userIds: allEventOrders?.map(order => order.user_id) || [],
            fullData: allEventOrders || []
          };

      // 3. Test the exact dashboard query path (admin_get_event_users)
      console.log('3. Testing admin_get_event_users RPC function...');
      const { data: adminEventUsers, error: adminEventError } = await supabase
        .rpc('admin_get_event_users', { event_uuid: eventId });
      
      results.adminGetEventUsers = adminEventError 
        ? `Error: ${adminEventError.message}` 
        : {
            count: adminEventUsers?.length || 0,
            userIds: adminEventUsers?.map(user => user.id) || [],
            fullData: adminEventUsers || []
          };

      // 4. Test the fallback dashboard query (admin_get_app_users)
      console.log('4. Testing admin_get_app_users RPC function...');
      const { data: adminAppUsers, error: adminAppError } = await supabase
        .rpc('admin_get_app_users', { event_uuid: eventId });
      
      results.adminGetAppUsers = adminAppError 
        ? `Error: ${adminAppError.message}` 
        : {
            count: adminAppUsers?.length || 0,
            userIds: adminAppUsers?.map(user => user.id) || [],
            fullData: adminAppUsers || []
          };

      // 5. Manual join query (what dashboard does as final fallback)
      console.log('5. Testing manual join query...');
      const { data: manualJoin, error: manualJoinError } = await supabase
        .from('event_orders')
        .select(`
          user_id,
          status,
          created_at,
          app_users!inner (
            id,
            first_name,
            last_name,
            email,
            user_type
          )
        `)
        .eq('event_id', eventId);
      
      results.manualJoinQuery = manualJoinError 
        ? `Error: ${manualJoinError.message}` 
        : {
            count: manualJoin?.length || 0,
            userIds: manualJoin?.map(record => record.user_id) || [],
            fullData: manualJoin || []
          };

      // 6. Check app_users table directly for users with this event_id
      console.log('6. Checking app_users with event_id...');
      const { data: appUsersWithEventId, error: appUsersError } = await supabase
        .from('app_users')
        .select('*')
        .eq('event_id', eventId);
      
      results.appUsersWithEventId = appUsersError 
        ? `Error: ${appUsersError.message}` 
        : {
            count: appUsersWithEventId?.length || 0,
            userIds: appUsersWithEventId?.map(user => user.id) || [],
            fullData: appUsersWithEventId || []
          };

      // 7. Analysis: Compare the results
      console.log('7. Analyzing discrepancies...');
      const eventOrderUserIds = results.allEventOrdersRecords?.userIds || [];
      const dashboardUserIds = results.adminGetEventUsers?.userIds || [];
      
      const missingFromDashboard = eventOrderUserIds.filter(id => !dashboardUserIds.includes(id));
      const extraInDashboard = dashboardUserIds.filter(id => !eventOrderUserIds.includes(id));
      
      results.discrepancyAnalysis = {
        eventOrdersCount: eventOrderUserIds.length,
        dashboardCount: dashboardUserIds.length,
        missingFromDashboard: missingFromDashboard,
        extraInDashboard: extraInDashboard,
        explanation: missingFromDashboard.length > 0 
          ? `Dashboard is missing ${missingFromDashboard.length} users. Missing user IDs: ${missingFromDashboard.join(', ')}`
          : 'No missing users found in dashboard query'
      };

      // 8. Check RLS policies by trying to fetch the missing users directly
      if (missingFromDashboard.length > 0) {
        console.log('8. Checking missing users in app_users table...');
        const { data: missingUsers, error: missingUsersError } = await supabase
          .from('app_users')
          .select('*')
          .in('id', missingFromDashboard);
        
        results.missingUsersCheck = missingUsersError 
          ? `Error: ${missingUsersError.message}` 
          : {
              found: missingUsers?.length || 0,
              expected: missingFromDashboard.length,
              data: missingUsers || []
            };
      }

    } catch (error) {
      console.error('Error during database check:', error);
      results.error = error.message;
    }
    
    setResults(results);
    setLoading(false);
  };

  return (
    <Box p={6} bg="white" borderRadius="md" boxShadow="md">
      <VStack spacing={4} align="stretch">
        <Heading size="md">Debug Dashboard Query Discrepancy - Event: {eventId}</Heading>
        
        <Alert status="info">
          <AlertIcon />
          <Text fontSize="sm">
            This tool compares what's in the database vs what the dashboard shows to find missing registrants.
          </Text>
        </Alert>
        
        <Button onClick={checkDatabase} isLoading={loading} colorScheme="blue">
          Analyze Query Discrepancy
        </Button>
        
        {results && (
          <Box>
            <Text fontWeight="bold" mb={2}>Results:</Text>
            <Code p={4} display="block" whiteSpace="pre-wrap" fontSize="sm">
              {JSON.stringify(results, null, 2)}
            </Code>
          </Box>
        )}
      </VStack>
    </Box>
  );
} 